import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert';
import { applyPageAnimations } from './utils/aosInit';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://servermain.sisrevolution.com';

const GUARDIAN_RELATIONSHIPS = [
  'Father',
  'Mother',
  'Stepfather',
  'Stepmother',
  'Uncle',
  'Aunt',
  'Grandfather',
  'Grandmother',
  'Brother',
  'Sister',
  'Legal Guardian',
  'Sponsor',
  'Other',
];

function normalizeClassName(row) {
  return String(row?.classname ?? row?.name ?? row?.class_ ?? '').trim();
}

function getAdmissionFeeFromClassRow(classRow) {
  if (!classRow || !classRow.othercharges) return null;
  let charges = classRow.othercharges;
  if (Array.isArray(charges) && charges.length > 0 && Array.isArray(charges[0])) {
    charges = charges[0];
  }
  if (!Array.isArray(charges)) return null;
  const admission = charges.find((c) =>
    String(c.description || '').toLowerCase().includes('admission')
  );
  if (!admission) return null;
  const amount = parseFloat(admission.amount);
  return Number.isFinite(amount) && amount >= 1 ? amount : null;
}

async function loadClassOptions(schoolId, admissionConfig) {
  const feeMap = {};
  (admissionConfig?.classes || []).forEach((c) => {
    if (c.classname) feeMap[c.classname] = c.feeGhs;
  });

  let schoolClasses = [];
  try {
    const res = await axios.post(`${API_BASE_URL}/api/class/getclassdata/`, { schoolID: schoolId });
    schoolClasses = Array.isArray(res.data) ? res.data : [];
  } catch (_) {
    schoolClasses = [];
  }

  const optionMap = new Map();

  schoolClasses.forEach((row) => {
    const name = normalizeClassName(row);
    if (!name) return;
    optionMap.set(name, {
      classname: name,
      feeGhs: feeMap[name] ?? getAdmissionFeeFromClassRow(row),
    });
  });

  (admissionConfig?.classes || []).forEach((c) => {
    if (c.classname && !optionMap.has(c.classname)) {
      optionMap.set(c.classname, { classname: c.classname, feeGhs: c.feeGhs });
    }
  });

  return Array.from(optionMap.values()).sort((a, b) =>
    a.classname.localeCompare(b.classname, undefined, { sensitivity: 'base' })
  );
}

const EMPTY_FORM = {
  lastname: '',
  othernames: '',
  gender: '',
  dob: '',
  hometown: '',
  residence: '',
  religion: '',
  studentIndex: '',
  classApplied: '',
  guardianRelationship: '',
  guardianlastname: '',
  guardianothernames: '',
  guardianoccupation: '',
  guardiancontact: '',
  guardianemail: '',
  guardianresidence: '',
  motherlastname: '',
  motherothernames: '',
  motheroccupation: '',
  mothercontact: '',
  motheremail: '',
  motherresidence: '',
  fatherlastname: '',
  fatherothernames: '',
  fatheroccupation: '',
  fathercontact: '',
  fatheremail: '',
  fatherresidence: '',
  applicantEmail: '',
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(8),
    minHeight: '70vh',
    backgroundColor: theme.palette.background.default,
  },
  hero: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  accentBar: {
    width: '25%',
    margin: '12px auto',
    border: 'none',
    height: 4,
    backgroundImage: 'linear-gradient(300deg, #17dddd 0%, #1760d3 100%)',
  },
  paper: {
    padding: theme.spacing(3),
    borderRadius: 10,
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    color: '#1760d3',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  schoolCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  submitBtn: {
    backgroundImage: 'linear-gradient(300deg, #17dddd 0%, #1760d3 100%)',
    color: 'white',
    padding: theme.spacing(1.5, 4),
    '&:hover': {
      backgroundImage: 'linear-gradient(300deg, #14c4c4 0%, #1450b8 100%)',
    },
  },
  successBox: {
    padding: theme.spacing(3),
    backgroundColor: '#e8f5e9',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
}));

function Apply() {
  const classes = useStyles();
  const { schoolID } = useParams();
  const location = useLocation();
  const history = useHistory();

  const [schools, setSchools] = useState([]);
  const [config, setConfig] = useState(null);
  const [classOptions, setClassOptions] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadingSchools, setLoadingSchools] = useState(!schoolID);
  const [loadingConfig, setLoadingConfig] = useState(!!schoolID);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [completedApp, setCompletedApp] = useState(null);
  const [statusQuery, setStatusQuery] = useState({ applicationNumber: '', phone: '' });
  const [statusResult, setStatusResult] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    applyPageAnimations({ duration: 700 });
  }, []);

  const loadSchools = useCallback(async () => {
    setLoadingSchools(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admissions/public/schools`);
      setSchools(res.data.schools || []);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Could not load schools. The admissions service may be temporarily unavailable.';
      swal('Unable to load schools', message, 'error');
      setSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  }, []);

  const loadConfig = useCallback(async (id) => {
    setLoadingConfig(true);
    setClassOptions([]);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admissions/public/config/${encodeURIComponent(id)}`);
      const admissionConfig = res.data;
      setConfig(admissionConfig);

      const options = await loadClassOptions(id, admissionConfig);
      setClassOptions(options);

      if (!admissionConfig.admissionsOpen) {
        swal('Admissions closed', 'This school is not currently accepting online applications.', 'warning');
      } else if (options.length === 0) {
        swal(
          'No classes found',
          'This school has no classes set up yet. Please contact the school administration.',
          'warning'
        );
      }
    } catch (err) {
      const message = err.response?.data?.message || 'School not found or admissions are unavailable.';
      swal('School not found', message, 'error');
      setConfig(null);
      setClassOptions([]);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    if (!schoolID) {
      loadSchools();
      return;
    }
    loadConfig(schoolID);
  }, [schoolID, loadSchools, loadConfig]);

  const verifyPayment = useCallback(async (reference) => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admissions/public/paystack-verify`, { reference });
      const app = res.data.application;
      setCompletedApp({
        applicationNumber: app.applicationNumber,
        status: app.status,
        classApplied: app.classApplied,
        lastname: app.lastname,
        othernames: app.othernames,
      });
      swal(
        'Application submitted',
        `Your application number is ${app.applicationNumber}. The school will review your application.`,
        'success'
      );
      history.replace(`/apply/${schoolID}`);
    } catch (err) {
      const message = err.response?.data?.message || 'Payment verification failed. Contact the school if you were charged.';
      swal('Verification failed', message, 'error');
    } finally {
      setVerifying(false);
    }
  }, [history, schoolID]);

  useEffect(() => {
    if (!schoolID) return;
    const params = new URLSearchParams(location.search);
    const reference = params.get('reference') || params.get('trxref');
    if (reference) {
      verifyPayment(reference);
    }
  }, [schoolID, location.search, verifyPayment]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schoolID || !config) return;

    if (!form.lastname.trim() || !form.othernames.trim()) {
      swal('Missing information', 'Student last name and other names are required.', 'warning');
      return;
    }
    if (!form.classApplied) {
      swal('Missing information', 'Please select a class.', 'warning');
      return;
    }
    if (!form.guardiancontact.trim()) {
      swal('Missing information', 'Guardian contact is required.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const callbackUrl = `${window.location.origin}/apply/${encodeURIComponent(schoolID)}`;
      const res = await axios.post(`${API_BASE_URL}/api/admissions/public/start`, {
        schoolID,
        ...form,
        callbackUrl,
      });

      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
        return;
      }
      swal('Error', 'Could not start payment. Please try again.', 'error');
    } catch (err) {
      const message = err.response?.data?.message || 'Could not submit application. Please try again.';
      swal('Submission failed', message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusLookup = async (e) => {
    e.preventDefault();
    if (!statusQuery.applicationNumber.trim() || !statusQuery.phone.trim()) {
      swal('Missing information', 'Application number and guardian phone are required.', 'warning');
      return;
    }
    setStatusLoading(true);
    setStatusResult(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admissions/public/status`, {
        params: {
          applicationNumber: statusQuery.applicationNumber.trim(),
          phone: statusQuery.phone.trim(),
        },
      });
      setStatusResult(res.data);
    } catch (err) {
      const message = err.response?.data?.message || 'Could not find that application.';
      swal('Status lookup failed', message, 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  const selectedClass = classOptions.find((c) => c.classname === form.classApplied);

  if (!schoolID) {
    return (
      <Container maxWidth="md" className={classes.root}>
        <div className={classes.hero}>
          <Typography variant="h4" component="h1" style={{ color: '#1760d3', fontWeight: 700 }}>
            Apply for Admission
          </Typography>
          <hr className={classes.accentBar} />
          <Typography variant="body1" color="textSecondary">
            Parents and guardians can register students online at schools using SIS Revolution.
          </Typography>
        </div>

        {loadingSchools ? (
          <Box textAlign="center" py={6}>
            <CircularProgress />
          </Box>
        ) : schools.length === 0 ? (
          <Paper className={classes.paper}>
            <Typography align="center" color="textSecondary">
              No schools are currently accepting online applications. Please check back later or contact your school
              directly.
            </Typography>
          </Paper>
        ) : (
          schools.map((school) => (
            <Paper key={school.schoolID} className={classes.schoolCard} elevation={1}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Typography variant="h6" style={{ color: '#1760d3' }}>
                    {school.schoolname}
                  </Typography>
                  {school.academicYear && (
                    <Typography variant="body2" color="textSecondary">
                      Academic year: {school.academicYear}
                    </Typography>
                  )}
                  {school.address && (
                    <Typography variant="body2" color="textSecondary">
                      {school.address}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary">
                    {school.classCount} class{school.classCount === 1 ? '' : 'es'} open for application
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} style={{ textAlign: 'right' }}>
                  <Button
                    component={Link}
                    to={`/apply/${encodeURIComponent(school.schoolID)}`}
                    variant="contained"
                    className={classes.submitBtn}
                  >
                    Apply now
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          ))
        )}

        <StatusLookupSection
          classes={classes}
          statusQuery={statusQuery}
          setStatusQuery={setStatusQuery}
          statusResult={statusResult}
          statusLoading={statusLoading}
          onSubmit={handleStatusLookup}
        />
      </Container>
    );
  }

  if (loadingConfig || verifying) {
    return (
      <Container maxWidth="md" className={classes.root}>
        <Box textAlign="center" py={8}>
          <CircularProgress />
          <Typography style={{ marginTop: 16 }} color="textSecondary">
            {verifying ? 'Confirming your payment…' : 'Loading application form…'}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!config) {
    return (
      <Container maxWidth="md" className={classes.root}>
        <Paper className={classes.paper}>
          <Typography align="center" gutterBottom>
            This school&apos;s application page is not available.
          </Typography>
          <Box textAlign="center" mt={2}>
            <Button component={Link} to="/apply" color="primary" variant="outlined">
              View all schools
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className={classes.root}>
      <div className={classes.hero}>
        <Typography variant="h4" component="h1" style={{ color: '#1760d3', fontWeight: 700 }}>
          Student Application
        </Typography>
        <hr className={classes.accentBar} />
        <Typography variant="h6">{config.schoolname}</Typography>
        {config.academicYear && (
          <Typography variant="body2" color="textSecondary">
            Academic year: {config.academicYear}
          </Typography>
        )}
        <Box mt={1}>
          <Button component={Link} to="/apply" size="small" color="primary">
            ← Choose another school
          </Button>
        </Box>
      </div>

      {completedApp && (
        <Box className={classes.successBox}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Application received
          </Typography>
          <Typography variant="body2">
            Application number: <strong>{completedApp.applicationNumber}</strong>
          </Typography>
          <Typography variant="body2">
            Student: {completedApp.lastname} {completedApp.othernames} — {completedApp.classApplied}
          </Typography>
          <Typography variant="body2">Status: {completedApp.status}</Typography>
        </Box>
      )}

      {!config.admissionsOpen ? (
        <Paper className={classes.paper}>
          <Typography align="center" color="textSecondary">
            Admissions are currently closed for this school.
          </Typography>
        </Paper>
      ) : (
        <Paper className={classes.paper} component="form" onSubmit={handleSubmit}>
          <Typography className={classes.sectionTitle}>Student information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Student last name"
                value={form.lastname}
                onChange={handleChange('lastname')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Student other names"
                value={form.othernames}
                onChange={handleChange('othernames')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" size="small" className={classes.field}>
                <InputLabel>Gender</InputLabel>
                <Select label="Gender" value={form.gender} onChange={handleChange('gender')}>
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of birth"
                type="date"
                value={form.dob}
                onChange={handleChange('dob')}
                variant="outlined"
                size="small"
                className={classes.field}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hometown"
                value={form.hometown}
                onChange={handleChange('hometown')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Residence"
                value={form.residence}
                onChange={handleChange('residence')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Religion"
                value={form.religion}
                onChange={handleChange('religion')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="BECE / index number (if any)"
                value={form.studentIndex}
                onChange={handleChange('studentIndex')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl required fullWidth variant="outlined" size="small" className={classes.field}>
                <InputLabel id="class-applied-label">Class applying for</InputLabel>
                <Select
                  labelId="class-applied-label"
                  id="class-applied"
                  label="Class applying for"
                  value={form.classApplied}
                  onChange={handleChange('classApplied')}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select a class</em>
                  </MenuItem>
                  {classOptions.map((cls) => (
                    <MenuItem key={cls.classname} value={cls.classname}>
                      {cls.feeGhs != null
                        ? `${cls.classname} — GHS ${cls.feeGhs} admission fee`
                        : cls.classname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {classOptions.length === 0 && (
                <Typography variant="caption" color="error">
                  No classes are available for this school yet.
                </Typography>
              )}
              {selectedClass?.feeGhs != null && (
                <Typography variant="caption" color="textSecondary">
                  You will pay GHS {selectedClass.feeGhs} via Paystack after submitting this form.
                </Typography>
              )}
              {selectedClass && selectedClass.feeGhs == null && (
                <Typography variant="caption" color="textSecondary">
                  Admission fee for this class will be confirmed by the school.
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider style={{ margin: '24px 0' }} />

          <Typography className={classes.sectionTitle}>Guardian information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" size="small" className={classes.field}>
                <InputLabel id="guardian-relationship-label">Relationship to student</InputLabel>
                <Select
                  labelId="guardian-relationship-label"
                  id="guardian-relationship"
                  label="Relationship to student"
                  value={form.guardianRelationship}
                  onChange={handleChange('guardianRelationship')}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select relationship</em>
                  </MenuItem>
                  {GUARDIAN_RELATIONSHIPS.map((relationship) => (
                    <MenuItem key={relationship} value={relationship}>
                      {relationship}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Guardian phone"
                value={form.guardiancontact}
                onChange={handleChange('guardiancontact')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guardian last name"
                value={form.guardianlastname}
                onChange={handleChange('guardianlastname')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guardian other names"
                value={form.guardianothernames}
                onChange={handleChange('guardianothernames')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guardian occupation"
                value={form.guardianoccupation}
                onChange={handleChange('guardianoccupation')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guardian email"
                type="email"
                value={form.guardianemail}
                onChange={handleChange('guardianemail')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guardian residence"
                value={form.guardianresidence}
                onChange={handleChange('guardianresidence')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Your email (for payment receipt)"
                type="email"
                value={form.applicantEmail}
                onChange={handleChange('applicantEmail')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: '24px 0' }} />

          <Typography className={classes.sectionTitle}>Parent details (optional)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mother last name"
                value={form.motherlastname}
                onChange={handleChange('motherlastname')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mother other names"
                value={form.motherothernames}
                onChange={handleChange('motherothernames')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mother contact"
                value={form.mothercontact}
                onChange={handleChange('mothercontact')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mother email"
                type="email"
                value={form.motheremail}
                onChange={handleChange('motheremail')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Father last name"
                value={form.fatherlastname}
                onChange={handleChange('fatherlastname')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Father other names"
                value={form.fatherothernames}
                onChange={handleChange('fatherothernames')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Father contact"
                value={form.fathercontact}
                onChange={handleChange('fathercontact')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Father email"
                type="email"
                value={form.fatheremail}
                onChange={handleChange('fatheremail')}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </Grid>
          </Grid>

          <Box textAlign="center" mt={3}>
            <Button
              type="submit"
              variant="contained"
              className={classes.submitBtn}
              disabled={submitting || classOptions.length === 0}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Continue to payment'}
            </Button>
          </Box>
        </Paper>
      )}

      <StatusLookupSection
        classes={classes}
        statusQuery={statusQuery}
        setStatusQuery={setStatusQuery}
        statusResult={statusResult}
        statusLoading={statusLoading}
        onSubmit={handleStatusLookup}
      />
    </Container>
  );
}

function StatusLookupSection({
  classes,
  statusQuery,
  setStatusQuery,
  statusResult,
  statusLoading,
  onSubmit,
}) {
  return (
    <Paper className={classes.paper}>
      <Typography className={classes.sectionTitle}>Check application status</Typography>
      <form onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Application number"
              value={statusQuery.applicationNumber}
              onChange={(e) => setStatusQuery((q) => ({ ...q, applicationNumber: e.target.value }))}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Guardian phone"
              value={statusQuery.phone}
              onChange={(e) => setStatusQuery((q) => ({ ...q, phone: e.target.value }))}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
        <Box mt={2}>
          <Button type="submit" variant="outlined" color="primary" disabled={statusLoading}>
            {statusLoading ? <CircularProgress size={22} /> : 'Look up status'}
          </Button>
        </Box>
      </form>
      {statusResult && (
        <Box mt={2} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
          <Typography variant="body2">
            <strong>{statusResult.lastname} {statusResult.othernames}</strong> — {statusResult.classApplied}
          </Typography>
          <Typography variant="body2">Status: {statusResult.status}</Typography>
          <Typography variant="body2">Application #: {statusResult.applicationNumber}</Typography>
          {statusResult.studentID && (
            <Typography variant="body2">Student ID (if enrolled): {statusResult.studentID}</Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default Apply;
